/* eslint-disable react/prop-types */
import { createContext, useCallback, useContext, useMemo, useReducer, useRef } from 'react';
import { get, keys, map, mapKeys, uniqueId } from 'lodash';

const DataStoreContext = createContext();
const DataStoreDispatchContext = createContext();

/**
 * @typedef DataStoreState
 * @property {number} requestId
 * @property {number} lastRequestedRowIdx
 * @property {number} lastRowIdx
 * @property {Object.<string, object>} rows
 * @property {number} total
 * @property {number} countOfOn
 * @property {number} countOfOff
 * @property {number} countOfEnabled
 * @property {number} countOfDisabled
 * @property {Error|string} error?
 * @property {boolean} isLoading
 * @property {boolean} hasLoaded
 * @property {boolean} clearRowsOnNextLoad?
 * @property {string} cursor?
 */

const ACTIONS = {
    INIT_REQUEST: 'initrequest',
    MERGE_ROWS: 'mergerows',
    SET_ERROR: 'seterror',
    HARD_RESET: 'hardreset',
    SOFT_RESET: 'softreset',
};

const ActionHandlers = {
    [ACTIONS.INIT_REQUEST]: initRequestAction,
    [ACTIONS.MERGE_ROWS]: mergeRowsAction,
    [ACTIONS.SET_ERROR]: setErrorAction,
    [ACTIONS.HARD_RESET]: hardResetAction,
    [ACTIONS.SOFT_RESET]: softResetAction,
};

export default function DataStoreProvider(props) {
    const { children, options, ...events } = props;

    const [state, dispatch] = useReducer(reducer, null, buildInitialState);

    // we only want to load missing data when we detect that we have gaps in our
    // dataset and there are no changes to our loading params. If we detect changes, we
    // do an internal reset to clear everything as this likely means the data we've already
    // loaded is no longer valid. After resetting the store, React will call the DataStoreProvider
    // again, and at that point changes will have settled down and storeParamsHaveChanged will
    // be false meaning we're in the clear to load missing data again
    const storeParamsHaveChanged = useHasStoreParamsChanged(dispatch, options, events);
    if (!storeParamsHaveChanged) {
        loadMissingRows(state, dispatch, options);
    }

    return (
        <DataStoreContext.Provider value={state}>
            <DataStoreDispatchContext.Provider value={dispatch}>
                {typeof children === 'function' ? children(state) : children}
            </DataStoreDispatchContext.Provider>
        </DataStoreContext.Provider>
    );
}

// ------------------------------------------------------------------
// Hooks

/**
 * @returns {DataStoreState}
 */
export function useDataStore() {
    return useContext(DataStoreContext);
}

function useDataStoreDispatch() {
    return useContext(DataStoreDispatchContext);
}

export function useTotalLoadedRows() {
    const { rows } = useDataStore();

    return useMemo(() => keys(rows).length, [rows]);
}

export function DataStoreConsumer(props) {
    const { children } = props;
    const data = useDataStore();

    return children(data);
}

export function useDataStoreReset() {
    const dispatch = useDataStoreDispatch();

    return useCallback(
        (hardReset) => {
            runAction(dispatch, hardReset === true ? ACTIONS.HARD_RESET : ACTIONS.SOFT_RESET);
        },
        [dispatch]
    );
}

// ------------------------------------------------------------------
// Functions

/**
 * @param {DataStoreState} state
 * @param {{ type: string, params?: object }} action
 * @returns {DataStoreState}
 */
function reducer(state, action) {
    const { type, params } = action;

    const handler = ActionHandlers[type];

    if (typeof handler !== 'function') {
        throw new Error(`Invalid reducer action.type: "${type}"`);
    }

    return handler(state, params);
}

function useHasStoreParamsChanged(dispatch, options, events) {
    const { limit, filters, sorters, params, loader } = options;

    const eventHandlers = useRef();
    eventHandlers.current = {
        onLimitChange: events?.onLimitChange,
        onFiltersChange: events?.onFiltersChange,
        onSortersChange: events?.onSortersChange,
        onParamsChange: events?.onParamsChange,
        onLoaderChange: events?.onLoaderChange,
    };

    const currentResetItems = { limit, filters, sorters, params, loader };
    const resetItems = useRef(currentResetItems);
    const { current: last } = resetItems;
    let hasChange = false;

    // do some synchronous manual change detection here
    if (last.limit !== limit) {
        invokeListenerCallback(eventHandlers.current.onLimitChange, limit);
    }

    if (last.filters !== filters) {
        invokeListenerCallback(eventHandlers.current.onFiltersChange, filters);
    }

    if (last.sorters !== sorters) {
        invokeListenerCallback(eventHandlers.current.onSortersChange, sorters);
    }

    if (last.params !== params) {
        invokeListenerCallback(eventHandlers.current.onParamsChange, params);
    }

    if (last.loader !== loader) {
        invokeListenerCallback(eventHandlers.current.onLoaderChange, loader);
    }

    if (last.limit !== limit || last.filters !== filters || last.sorters !== sorters) {
        runAction(dispatch, ACTIONS.HARD_RESET, { limit, filters, sorters });
        hasChange = true;
    } else if (last.loader !== loader || last.params !== params) {
        runAction(dispatch, ACTIONS.SOFT_RESET);
        hasChange = true;
    }

    resetItems.current = currentResetItems;

    return hasChange;
}

/**
 * The meat-n-potatoes of the DataStore. Given the current state and rowBounds,
 * find out what we need to load. Use the provided loader and its options to actually
 * load the data, provided that there is a loader
 * @param {Object} state
 * @param {Function} dispatch
 * @param {Object} options
 */
async function loadMissingRows(state, dispatch, options) {
    const { loader, filters, sorters, limit, deletedMapping } = options;

    if (typeof loader !== 'function') return;

    let { rowBounds } = options;
    const { hasLoaded, isLoading, clearRowsOnNextLoad } = state;

    // if our last dataset was empty, we'll need to manually tweak the rowBounds to load the next
    // rows when we want more data to come in.
    let [startBound, endBound] = rowBounds || [];
    if ((!hasLoaded || clearRowsOnNextLoad) && !isLoading && startBound === 0 && endBound === 0) {
        rowBounds = [0, 1];
        [startBound, endBound] = rowBounds;
    }

    // don't continue loading if we already are...
    if (isLoading) return;

    const { lastRequestedRowIdx, cursor } = state;

    // no-op, we already have the latest data being fetched...
    if (endBound <= lastRequestedRowIdx) return;

    // at this point in the function, we know we have data to load data
    const requestId = uniqueId();

    const startRowIdx = lastRequestedRowIdx;
    const endRowIdx = startRowIdx + limit;

    // tell the DT we're about to load...
    runAction(dispatch, ACTIONS.INIT_REQUEST, { endRowIdx, requestId });

    try {
        const data = await loader({
            filters,
            sorters,
            start: startRowIdx,
            limit,
            cursor,
        });

        const mergeParams = {
            startRowIdx,
            endRowIdx,
            requestId,
            deletedMapping,
            data,
        };

        // merge our data into the store now
        runAction(dispatch, ACTIONS.MERGE_ROWS, mergeParams);
    } catch (error) {
        runAction(dispatch, ACTIONS.SET_ERROR, { error });
    }
}

/**
 * @returns {DataStoreState}
 */
function buildInitialState() {
    return {
        requestId: null,
        lastRequestedRowIdx: 0,
        lastRowIdx: 0,
        rows: {},
        total: 0,
        countOfOn: -1,
        countOfOff: -1,
        countOfEnabled: -1,
        countOfDisabled: -1,
        error: null,
        isLoading: false,
        hasLoaded: false,
        clearRowsOnNextLoad: false,
        cursor: null,
    };
}

// ------------------------------------------------------------------
// Reducer Actions

function initRequestAction(state, { endRowIdx, requestId }) {
    return updateState(state, {
        requestId,
        lastRequestedRowIdx: endRowIdx,
        isLoading: true,
    });
}

function softResetAction(state) {
    return updateState(state, {
        requestId: null,
        lastRequestedRowIdx: 0,
        lastRowIdx: 0,
        isLoading: false,
        error: null,
        clearRowsOnNextLoad: true,
        cursor: null,
    });
}

function hardResetAction(state) {
    return updateState(softResetAction(state), {
        rows: {},
        total: 0,
        countOfOn: -1,
        countOfOff: -1,
        countOfEnabled: -1,
        countOfDisabled: -1,
        hasLoaded: false,
        clearRowsOnNextLoad: false,
    });
}

function mergeRowsAction(state, { startRowIdx, requestId, deletedMapping, data }) {
    const { rows, clearRowsOnNextLoad, requestId: lastRequestId } = state;

    // no-op if this is request has been invalidated or if data is missing
    if (lastRequestId !== requestId || !data) {
        return state;
    }

    const { rows: newRowsRaw, length, countOfOn, countOfOff, countOfEnabled, countOfDisabled, cursor } = data;

    const newRows = buildPartialDataSet(newRowsRaw, {
        startRowIdx,
        deletedMapping,
    });

    const currentRows = clearRowsOnNextLoad ? {} : rows;

    return updateState(state, {
        cursor,
        countOfOn,
        countOfOff,
        countOfEnabled,
        countOfDisabled,
        total: length,
        rows: { ...currentRows, ...newRows },
        requestId: null,
        isLoading: false,
        hasLoaded: true,
        clearRowsOnNextLoad: false,
    });
}

function setErrorAction(state, { error }) {
    return updateState(state, { error });
}

// ------------------------------------------------------------------
// Helper Functions

function updateState(state, updates) {
    return { ...state, ...updates };
}

function buildPartialDataSet(rows, { startRowIdx, deletedMapping }) {
    // Assign each row a unique ID as well as whether or not it has been deleted
    const wrappedRows = map(rows, (row) => ({
        row,
        id: uniqueId(),
        deleted: get(row, deletedMapping, false),
    }));

    return mapKeys(wrappedRows, (_value, idx) => parseInt(idx, 10) + startRowIdx);
}

function runAction(dispatch, type, params) {
    dispatch({ type, params });
}

/**
 * Invokes a callback (which may not be present) asynchronously,
 * as the change listeners are executed synchronously which will cause
 * issues if we change anything currently being rendered.
 *
 * @param {Function} [callback] The callback function to call (optional)
 * @param  {...any} [params]
 */
function invokeListenerCallback(callback, ...params) {
    if (typeof callback === 'function') {
        // execute on next async tick
        setTimeout(() => callback(...params), 0);
    }
}
